import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Settings, Wifi, Network, Save, RefreshCw, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ONTProvisioning = () => {
  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [showAddWANModal, setShowAddWANModal] = useState(false);
  const [wanInterface, setWanInterface] = useState('1'); // WAN 1, 2, 3, etc
  const [existingWANs, setExistingWANs] = useState([]); // List of existing WAN interfaces
  const [checkingWANs, setCheckingWANs] = useState(false);
  
  const [config, setConfig] = useState({
    // WAN Interface
    wan_interface: '1', // WAN 1, 2, 3, etc
    
    // WAN Service Type (Purpose)
    wan_service_type: 'INTERNET', // INTERNET, TR069, BRIDGE, TR069_INTERNET, OTHER
    
    // WAN Configuration
    wan_mode: 'PPPoE',
    vlan_id: '10',
    pppoe_username: '',
    pppoe_password: '',
    static_ip: '',
    static_netmask: '',
    static_gateway: '',
    
    // WiFi Configuration
    wifi_ssid: '',
    wifi_password: '',
    wifi_security: 'WPA2-PSK',
    wifi_channel: 'auto',
    wifi_enabled: true,
    
    // Advanced
    dns_primary: '8.8.8.8',
    dns_secondary: '8.8.4.4',
    mtu: '1492'
  });

  useEffect(() => {
    fetchWANDevices();
  }, []);

  // Filter devices based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredDevices(devices);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = devices.filter(device => {
        // Decode URL encoded strings (e.g., %2D -> -, %20 -> space)
        const decodeStr = (str) => {
          try {
            return decodeURIComponent(str || '').toLowerCase();
          } catch {
            return (str || '').toLowerCase();
          }
        };
        
        const serialNumber = decodeStr(device.serial_number);
        const deviceId = decodeStr(device.device_id);
        const pppoeUsername = decodeStr(device.pppoe_username);
        const customerName = decodeStr(device.customer_name);
        const model = decodeStr(device.model);
        const manufacturer = decodeStr(device.manufacturer);
        
        // Also keep original (non-decoded) for matching
        const serialRaw = (device.serial_number || '').toLowerCase();
        const deviceIdRaw = (device.device_id || '').toLowerCase();
        
        // Extract last part of serial (after last hyphen) for easier search
        // E.g., "f86ce1-g99-xpon-ggcl2989285b" -> "ggcl2989285b"
        const serialParts = serialNumber.split('-');
        const shortSerial = serialParts[serialParts.length - 1] || '';
        
        // Match against both decoded and raw versions
        return serialNumber.includes(query) || 
               serialRaw.includes(query) ||
               deviceId.includes(query) || 
               deviceIdRaw.includes(query) ||
               shortSerial.includes(query) ||  // Search by short serial
               pppoeUsername.includes(query) ||
               customerName.includes(query) ||
               model.includes(query) ||
               manufacturer.includes(query);
      });
      setFilteredDevices(filtered);
    }
  }, [searchQuery, devices]);

  const fetchWANDevices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/wan-devices?page_size=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Get all ONT devices (FiberHome, HUAWEI, ZTE, etc)
        const allDevices = response.data.data.devices || [];
        setDevices(allDevices);
        setFilteredDevices(allDevices);
      }
    } catch (error) {
      toast.error('Failed to fetch devices');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeviceSelect = (device) => {
    setSelectedDevice(device);
    // Pre-fill with customer data if linked
    if (device.customer_id) {
      // TODO: Fetch customer PPPoE credentials
    }
    // Fetch existing WANs for this device
    checkExistingWANs(device.device_id);
  };

  const checkExistingWANs = async (deviceId) => {
    setCheckingWANs(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/ont/wan-status/${deviceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setExistingWANs(response.data.wan_interfaces || []);
        
        // Update selected device with WAN details
        if (response.data.wan_details && selectedDevice) {
          setSelectedDevice({
            ...selectedDevice,
            wan_details: response.data.wan_details
          });
        }
      } else {
        // If endpoint doesn't exist, assume empty
        setExistingWANs([]);
      }
    } catch (error) {
      // If error, assume no existing WANs (backward compatibility)
      console.log('Could not fetch existing WANs:', error);
      setExistingWANs([]);
    } finally {
      setCheckingWANs(false);
    }
  };

  const openAddWANModal = () => {
    if (!selectedDevice) {
      toast.error('Pilih device terlebih dahulu');
      return;
    }
    
    // Check existing WANs before opening modal
    checkExistingWANs(selectedDevice.device_id);
    setShowAddWANModal(true);
  };

  const handleAddWAN = () => {
    if (!selectedDevice) {
      toast.error('Pilih device terlebih dahulu');
      return;
    }
    
    // Check if WAN interface already exists
    if (existingWANs.includes(wanInterface)) {
      toast.error(`WAN ${wanInterface} sudah dikonfigurasi. Pilih interface lain atau hapus yang lama terlebih dahulu.`);
      return;
    }
    
    // Set WAN interface number in config
    setConfig({
      ...config,
      wan_interface: wanInterface
    });
    
    setShowAddWANModal(false);
    toast.success(`WAN ${wanInterface} dipilih untuk konfigurasi`);
  };

  const handleProvision = async () => {
    if (!selectedDevice) {
      toast.error('Pilih device terlebih dahulu');
      return;
    }

    // Validation
    if (config.wan_mode === 'PPPoE' && (!config.pppoe_username || !config.pppoe_password)) {
      toast.error('PPPoE username dan password harus diisi');
      return;
    }

    // WiFi Configuration is only required for INTERNET or OTHER with PPPoE
    const requireWifiConfig = 
      (config.wan_service_type === 'INTERNET' || config.wan_service_type === 'OTHER') && 
      config.wan_mode === 'PPPoE';

    if (requireWifiConfig) {
      if (!config.wifi_ssid || !config.wifi_password) {
        toast.error('WiFi SSID dan password harus diisi');
        return;
      }

      if (config.wifi_password.length < 8) {
        toast.error('WiFi password minimal 8 karakter');
        return;
      }
    }

    try {
      setProvisioning(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_URL}/api/ont/provision`,
        {
          device_id: selectedDevice.device_id,
          config: config
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Provisioning berhasil! Config akan diterapkan dalam 1-2 menit');
      } else {
        toast.error(response.data.message || 'Provisioning failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Provisioning failed');
      console.error(error);
    } finally {
      setProvisioning(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">ONT Provisioning</h1>
          <p className="text-slate-600 dark:text-slate-300">Auto-configure ONT devices (FiberHome, HUAWEI, ZTE) via TR-069</p>
        </div>
        <Button onClick={fetchWANDevices} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Device Selection */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Network className="w-4 h-4" />
                Select ONT Device
              </h3>
              <Button 
                onClick={openAddWANModal} 
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={!selectedDevice}
                title={selectedDevice ? "Add new WAN connection" : "Select device first"}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add WAN
              </Button>
            </div>
            
            {/* Search Box */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                <Input
                  type="text"
                  placeholder="Search serial number, device ID, customer..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10"
                />
              </div>
              {searchQuery && (
                <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
                  Found {filteredDevices.length} device(s)
                </p>
              )}
            </div>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : filteredDevices.length === 0 ? (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                <Network className="w-12 h-12 mx-auto mb-2 opacity-30" />
                {searchQuery ? (
                  <>
                    <p>No devices found matching "{searchQuery}"</p>
                    <p className="text-xs mt-2">Try different search term</p>
                  </>
                ) : (
                  <>
                    <p>No ONT devices found</p>
                    <p className="text-xs mt-2">Sync devices from WAN Devices page</p>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {filteredDevices.map((device) => (
                  <div
                    key={device.device_id}
                    onClick={() => handleDeviceSelect(device)}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      selectedDevice?.device_id === device.device_id
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:border-indigo-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{device.serial_number}</span>
                      <div className="flex gap-1">
                        {device.manufacturer && (
                          <Badge variant="outline" className="text-xs">
                            {device.manufacturer}
                          </Badge>
                        )}
                        <Badge className={device.online ? "bg-emerald-500" : "bg-slate-400"}>
                          {device.online ? "Online" : "Offline"}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-xs text-slate-600 dark:text-slate-300">
                      Model: {device.model || 'Unknown'}
                    </div>
                    {device.customer_name && (
                      <div className="text-xs text-indigo-600 mt-1 dark:text-indigo-400">
                        Customer: {device.customer_name}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Configuration Form */}
        <Card className="lg:col-span-2">
          <CardContent className="pt-6">
            {!selectedDevice ? (
              <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                <Settings className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Pilih device untuk mulai konfigurasi</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Device Info */}
                <div className="bg-slate-50 p-4 rounded-lg dark:bg-slate-900">
                  <h4 className="font-semibold mb-2">Selected Device</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Serial: {selectedDevice.serial_number}</div>
                    <div>Model: {selectedDevice.model}</div>
                    <div>IP: {selectedDevice.tr069_ip || 'N/A'}</div>
                    <div>Status: {selectedDevice.online ? '🟢 Online' : '🔴 Offline'}</div>
                  </div>
                </div>

                {/* Existing WAN Connections */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-blue-900/30">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                      <Network className="w-4 h-4" />
                      WAN Connections di ONT
                    </h4>
                    {checkingWANs && (
                      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </div>
                  
                  {checkingWANs ? (
                    <p className="text-sm text-blue-700 dark:text-blue-300">Checking existing WANs...</p>
                  ) : existingWANs.length === 0 ? (
                    <div className="text-center py-3">
                      <p className="text-sm text-blue-700 dark:text-blue-300">Belum ada WAN dikonfigurasi</p>
                      <p className="text-xs text-blue-600 mt-1 dark:text-blue-400">Klik "Add WAN" untuk menambahkan</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-3">
                        {['1', '2', '3', '4'].map((num) => {
                          const wanDetail = selectedDevice.wan_details?.[num];
                          // Check if WAN is actually enabled/configured
                          const isConfigured = wanDetail && wanDetail.enabled === true;
                          
                          return (
                            <div
                              key={num}
                              className={`p-3 rounded-lg border-2 ${
                                isConfigured
                                  ? 'border-emerald-500 bg-emerald-50'
                                  : 'border-slate-200 bg-slate-50'
                              }`}
                            >
                              <div className="flex items-start gap-2">
                                <Network className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                                  isConfigured ? 'text-emerald-600' : 'text-slate-300'
                                }`} />
                                <div className="flex-1 min-w-0">
                                  <p className={`text-xs font-bold ${
                                    isConfigured ? 'text-emerald-700' : 'text-slate-400'
                                  }`}>
                                    WAN {num}
                                  </p>
                                  
                                  {isConfigured && wanDetail ? (
                                    <div className="mt-1 space-y-1">
                                      <div className="flex items-center gap-1">
                                        <Badge className="bg-emerald-600 text-[9px] px-1.5 py-0">
                                          {wanDetail.type || wanDetail.wan_mode}
                                        </Badge>
                                        {wanDetail.vlan_id && (
                                          <Badge className="bg-blue-600 text-[9px] px-1.5 py-0">
                                            VLAN {wanDetail.vlan_id}
                                          </Badge>
                                        )}
                                      </div>
                                      
                                      {wanDetail.purpose && (
                                        <p className="text-[10px] text-emerald-700 font-medium dark:text-emerald-300">
                                          {wanDetail.purpose}
                                        </p>
                                      )}
                                      
                                      {wanDetail.ip_address && (
                                        <p className="text-[9px] text-slate-600 truncate dark:text-slate-300" title={wanDetail.ip_address}>
                                          IP: {wanDetail.ip_address}
                                        </p>
                                      )}
                                      
                                      {wanDetail.username && (
                                        <p className="text-[9px] text-slate-600 truncate dark:text-slate-300" title={wanDetail.username}>
                                          User: {wanDetail.username}
                                        </p>
                                      )}
                                      
                                      {wanDetail.connection_name && (
                                        <p className="text-[9px] text-slate-500 truncate dark:text-slate-400" title={wanDetail.connection_name}>
                                          {wanDetail.connection_name}
                                        </p>
                                      )}
                                    </div>
                                  ) : isConfigured ? (
                                    <Badge className="bg-emerald-600 text-[9px] px-1.5 py-0 mt-1">
                                      Active
                                    </Badge>
                                  ) : (
                                    <p className="text-[10px] text-slate-400 mt-1 dark:text-slate-500">Available</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      <div className="text-xs text-blue-700 mt-2 dark:text-blue-300">
                        ✓ {existingWANs.length} WAN interface aktif: {existingWANs.join(', ')}
                      </div>
                    </div>
                  )}
                </div>

                {/* WAN Configuration */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Network className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      WAN Configuration
                    </h3>
                    <Badge className="bg-emerald-600">WAN {config.wan_interface}</Badge>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>WAN Service Type / Purpose <span className="text-red-500">*</span></Label>
                      <select
                        value={config.wan_service_type}
                        onChange={(e) => setConfig({...config, wan_service_type: e.target.value})}
                        className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg dark:border-slate-600"
                      >
                        <option value="INTERNET">Internet</option>
                        <option value="TR069">TR069 (Management)</option>
                        <option value="BRIDGE">Bridge</option>
                        <option value="TR069_INTERNET">TR069 + Internet</option>
                        <option value="IPTV">IPTV</option>
                        <option value="VOIP">VoIP</option>
                        <option value="OTHER">Other</option>
                      </select>
                      <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Tujuan penggunaan WAN interface</p>
                    </div>

                    <div>
                      <Label>WAN Mode / Connection Type</Label>
                      <select
                        value={config.wan_mode}
                        onChange={(e) => setConfig({...config, wan_mode: e.target.value})}
                        className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg dark:border-slate-600"
                      >
                        <option value="PPPoE">PPPoE (Route)</option>
                        <option value="DHCP">DHCP</option>
                        <option value="Static">Static IP</option>
                        <option value="Bridge">Bridge Mode</option>
                      </select>
                    </div>

                    <div>
                      <Label>VLAN ID</Label>
                      <Input
                        type="number"
                        value={config.vlan_id}
                        onChange={(e) => setConfig({...config, vlan_id: e.target.value})}
                        placeholder="10"
                      />
                    </div>

                    {config.wan_mode === 'PPPoE' && (
                      <>
                        <div>
                          <Label>PPPoE Username <span className="text-red-500">*</span></Label>
                          <Input
                            value={config.pppoe_username}
                            onChange={(e) => setConfig({...config, pppoe_username: e.target.value})}
                            placeholder="username@isp.com"
                          />
                        </div>

                        <div>
                          <Label>PPPoE Password <span className="text-red-500">*</span></Label>
                          <Input
                            type="password"
                            value={config.pppoe_password}
                            onChange={(e) => setConfig({...config, pppoe_password: e.target.value})}
                            placeholder="••••••••"
                          />
                        </div>
                      </>
                    )}

                    {config.wan_mode === 'Static' && (
                      <>
                        <div>
                          <Label>IP Address</Label>
                          <Input
                            value={config.static_ip}
                            onChange={(e) => setConfig({...config, static_ip: e.target.value})}
                            placeholder="192.168.1.100"
                          />
                        </div>

                        <div>
                          <Label>Netmask</Label>
                          <Input
                            value={config.static_netmask}
                            onChange={(e) => setConfig({...config, static_netmask: e.target.value})}
                            placeholder="255.255.255.0"
                          />
                        </div>

                        <div>
                          <Label>Gateway</Label>
                          <Input
                            value={config.static_gateway}
                            onChange={(e) => setConfig({...config, static_gateway: e.target.value})}
                            placeholder="192.168.1.1"
                          />
                        </div>
                      </>
                    )}

                    <div>
                      <Label>MTU</Label>
                      <Input
                        type="number"
                        value={config.mtu}
                        onChange={(e) => setConfig({...config, mtu: e.target.value})}
                        placeholder="1492"
                      />
                    </div>
                  </div>
                </div>

                {/* WiFi Configuration - Only show for INTERNET or OTHER with PPPoE */}
                {((config.wan_service_type === 'INTERNET' || config.wan_service_type === 'OTHER') && 
                  config.wan_mode === 'PPPoE') && (
                  <div>
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Wifi className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      WiFi Configuration
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>WiFi SSID <span className="text-red-500">*</span></Label>
                        <Input
                          value={config.wifi_ssid}
                          onChange={(e) => setConfig({...config, wifi_ssid: e.target.value})}
                          placeholder="MyWiFi"
                        />
                      </div>

                      <div>
                        <Label>WiFi Password <span className="text-red-500">*</span></Label>
                        <Input
                          type="password"
                          value={config.wifi_password}
                          onChange={(e) => setConfig({...config, wifi_password: e.target.value})}
                          placeholder="Min. 8 karakter"
                        />
                        <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Minimal 8 karakter</p>
                      </div>

                      <div>
                        <Label>Security Mode</Label>
                        <select
                          value={config.wifi_security}
                          onChange={(e) => setConfig({...config, wifi_security: e.target.value})}
                          className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg dark:border-slate-600"
                        >
                          <option value="WPA2-PSK">WPA2-PSK</option>
                          <option value="WPA3-PSK">WPA3-PSK</option>
                          <option value="WPA-PSK">WPA-PSK</option>
                        </select>
                      </div>

                      <div>
                        <Label>WiFi Channel</Label>
                        <select
                          value={config.wifi_channel}
                          onChange={(e) => setConfig({...config, wifi_channel: e.target.value})}
                          className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg dark:border-slate-600"
                        >
                          <option value="auto">Auto</option>
                          <option value="1">1</option>
                          <option value="6">6</option>
                          <option value="11">11</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* DNS Configuration */}
                <div>
                  <h3 className="font-semibold mb-4">DNS Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Primary DNS</Label>
                      <Input
                        value={config.dns_primary}
                        onChange={(e) => setConfig({...config, dns_primary: e.target.value})}
                        placeholder="8.8.8.8"
                      />
                    </div>

                    <div>
                      <Label>Secondary DNS</Label>
                      <Input
                        value={config.dns_secondary}
                        onChange={(e) => setConfig({...config, dns_secondary: e.target.value})}
                        placeholder="8.8.4.4"
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedDevice(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleProvision}
                    disabled={provisioning || !selectedDevice.online}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {provisioning ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Provisioning...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Apply Configuration
                      </>
                    )}
                  </Button>
                </div>

                {!selectedDevice.online && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 dark:bg-yellow-900/30">
                    ⚠️ Device offline - provisioning tidak dapat dilakukan
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add WAN Modal */}
      {showAddWANModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full dark:bg-slate-800">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-2xl font-bold">Add WAN Connection</h2>
              <p className="text-slate-600 mt-1 dark:text-slate-300">Pilih WAN interface untuk device ini</p>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-blue-900/30">
                <p className="text-sm text-blue-900 font-medium mb-1">Selected Device:</p>
                <p className="text-blue-700 dark:text-blue-300">{selectedDevice?.serial_number}</p>
                <p className="text-xs text-blue-600 mt-1 dark:text-blue-400">Model: {selectedDevice?.model}</p>
              </div>

              <div>
                <Label className="text-base font-semibold">WAN Interface</Label>
                <p className="text-sm text-slate-600 mb-3 dark:text-slate-300">Pilih nomor WAN interface yang akan dikonfigurasi</p>
                
                {checkingWANs ? (
                  <div className="text-center py-8">
                    <div className="w-6 h-6 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="text-sm text-slate-600 mt-2 dark:text-slate-300">Checking existing WANs...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {['1', '2', '3', '4'].map((num) => {
                      const isExisting = existingWANs.includes(num);
                      const isSelected = wanInterface === num;
                      
                      return (
                        <button
                          key={num}
                          onClick={() => !isExisting && setWanInterface(num)}
                          disabled={isExisting}
                          className={`p-4 rounded-lg border-2 transition-all relative ${
                            isExisting
                              ? 'border-red-200 bg-red-50 text-red-400 cursor-not-allowed'
                              : isSelected
                              ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                              : 'border-slate-200 hover:border-slate-300 text-slate-600'
                          }`}
                        >
                          <div className="text-center">
                            <Network className="w-6 h-6 mx-auto mb-1" />
                            <p className="text-xs font-medium">WAN {num}</p>
                            {isExisting && (
                              <p className="text-[9px] text-red-600 mt-1 dark:text-red-400">Already Used</p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                
                {existingWANs.length > 0 && !checkingWANs && (
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 dark:bg-amber-900/30">
                    <p className="text-sm text-amber-800">
                      ℹ️ WAN interface yang sudah dikonfigurasi: <strong>{existingWANs.join(', ')}</strong>
                    </p>
                  </div>
                )}
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 dark:bg-yellow-900/30">
                <h4 className="font-medium text-yellow-900 mb-2">⚠️ Perhatian:</h4>
                <ul className="text-sm text-yellow-800 space-y-1">
                  <li>• WAN 1 biasanya untuk internet utama</li>
                  <li>• WAN 2-4 untuk backup, IPTV, atau VPN</li>
                  <li>• Pastikan interface yang dipilih belum dikonfigurasi</li>
                  <li>• Config akan diterapkan via TR-069</li>
                </ul>
              </div>
            </div>

            <div className="p-6 bg-slate-50 border-t border-slate-200 flex justify-end gap-3 dark:bg-slate-900 dark:border-slate-700">
              <Button
                variant="outline"
                onClick={() => setShowAddWANModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddWAN}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Configure WAN {wanInterface}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ONTProvisioning;
